import type { Ref, RefObject } from 'react'
import Immutable from 'immutable'
import loadable from '@loadable/component'

import type { ImageEditorRef } from '../../../../ImageEditor/ImageEditor'
import LazyImage from '../../../../LazyImage'

const ImageEditor = loadable(() => import(/* webpackChunkName: "editor" */ '../../../../ImageEditor'), {
  resolveComponent: (components) => components.ImageEditor,
})

interface RenderImageOptions {
  data: Immutable.Map<string, any>
  config: any
  editorView: boolean
  editAction?: string | null
  imageEditPreviewSrc?: string
  imageRef: RefObject<HTMLImageElement | null>

  aspectRatioMap: Record<string, any>
  defaultData: Immutable.Map<string, any>

  onSave: (data: Immutable.Map<string, any>, shouldClose?: boolean) => void
  onCancel: () => void
  onDataChange: (data: Immutable.Map<string, any>) => void
  setImageEditPreviewSrc: (src: string | undefined) => void

  imageEditorRef?: Ref<ImageEditorRef>

  OverlayComponent?: React.ComponentType<any>
  overlayProps?: Record<string, any>

  wrapperClassName?: string
  wrapperStyle?: React.CSSProperties
  imageClassName?: string
  imageStyle?: React.CSSProperties

  isLinked?: boolean

  setImageHeight?: (height: number) => void
}

export function renderImage({
  data,
  config,
  editorView,
  editAction,
  imageEditPreviewSrc,
  imageRef,
  aspectRatioMap,
  defaultData,
  onSave,
  onCancel,
  onDataChange,
  setImageEditPreviewSrc,
  imageEditorRef,
  OverlayComponent,
  overlayProps = {},
  wrapperClassName = 'dali-image-wrapper',
  wrapperStyle = {},
  imageClassName = 'dali-image',
  imageStyle = {},
  isLinked = false,
  setImageHeight,
}: RenderImageOptions) {
  // We can only set the aspect ratio if we know the width and height of the
  // image. Older user-uploaded images don't have this information.
  const aspectRatio = data.get('width') && data.get('height') ? data.get('width') / data.get('height') : undefined

  const image = (
    <>
      <div
        className={imageClassName}
        style={{
          display: editAction === 'editImage' ? 'none' : imageStyle.display || 'block',
          clipPath: data.getIn(['edit', 'aspectRatio', 'label']) === 'circle' ? 'circle(50% at center)' : '',
          ...imageStyle,
        }}
      >
        {editorView && imageEditPreviewSrc ? (
          <img
            src={imageEditPreviewSrc}
            alt={data.get('alt')}
            style={{ width: '100%', aspectRatio: data.getIn(['edit', 'aspectRatio', 'value']), objectFit: 'cover' }}
          />
        ) : (
          <LazyImage
            src={config?.imageUrl ? config.imageUrl(data.get('src')) : data.get('src')}
            width={data.get('width')}
            height={data.get('height')}
            alt={data.get('alt')}
            innerRef={(img) => {
              if (img) {
                imageRef.current = img

                if (setImageHeight) {
                  const handleLoad = () => {
                    setImageHeight(img.offsetHeight)
                  }

                  if (img.complete) {
                    setImageHeight(img.offsetHeight)
                  } else {
                    img.addEventListener('load', handleLoad)
                  }

                  return () => {
                    img.removeEventListener('load', handleLoad)
                  }
                }
              }
            }}
          />
        )}
      </div>
      {editAction !== 'editImage' && OverlayComponent && (
        <OverlayComponent
          textSettings={{
            text: data.get('text'),
            headline: data.get('headline'),
            buttontext: data.get('buttontext'),
            buttonenabled: data.get('buttonenabled'),
          }}
          image={data}
          isCircle={data.getIn(['edit', 'aspectRatio', 'label']) === 'circle'}
          {...overlayProps}
        />
      )}
      {editAction === 'editImage' && (
        <ImageEditor
          ref={imageEditorRef}
          src={
            config?.imageUrl
              ? config.imageUrl(data.get('refSrc') || data.get('src'))
              : data.get('refSrc') || data.get('src')
          }
          previewSrc={imageEditPreviewSrc || imageRef.current?.currentSrc}
          aspectRatioMap={aspectRatioMap}
          initial={!data.get('refSrc')}
          edit={
            data.get('edit')?.toJS() ||
            // This fallback is needed for images that were created before
            // the image editor feature was introduced. To convert to the
            // image editor, we initialize with the aspect ratio of the
            // image so there's no visual change.
            defaultData
              .get('edit')
              .set('aspectRatio', {
                label: 'original',
                value: aspectRatio,
              })
              .toJS()
          }
          onInit={async (edit, imageFile) => {
            // Absence of "refSrc" indicates that the image is initially
            // auto-edited with the image editor. To save the auto-edited
            // image whilst keeping the original image, we save the edited
            // image and set the current value of "src" to "refSrc".
            if (!data.get('refSrc')) {
              if (config?.storeFile) {
                const imageUploadResponse = await config.storeFile(imageFile)
                setImageEditPreviewSrc(await getDataURLFromFile(imageFile))
                onSave(
                  data.merge(
                    Immutable.fromJS({
                      edit,
                      src: imageUploadResponse.absoluteDownloadUrl,
                      width: imageUploadResponse.width,
                      height: imageUploadResponse.height,
                      refSrc: data.get('src'),
                    }),
                  ),
                  true,
                )
              }
            }
          }}
          onCancel={onCancel}
          onStateChange={(edit) => {
            // We're only interested in any aspect ratio changes since we then need to
            // re-render with updated aspect ratio styles affecting layout.
            if (data.getIn(['edit', 'aspectRatio', 'value']) !== edit.aspectRatio.value) {
              onDataChange(data.merge(Immutable.fromJS({ edit })))
            }
          }}
          onChange={async (edit, imageFile, newImageSourceFile) => {
            if (config?.storeFile) {
              const storeFile = config.storeFile
              const imageUploads = [storeFile(imageFile)]
              if (newImageSourceFile && newImageSourceFile !== imageFile) {
                imageUploads.push(storeFile(newImageSourceFile))
              }
              const imageUploadResponses = await Promise.all(imageUploads)
              const imageUploadResponse = imageUploadResponses[0]
              const newSourceImageUploadResponse =
                imageFile === newImageSourceFile ? imageUploadResponses[0] : imageUploadResponses[1]
              setImageEditPreviewSrc(await getDataURLFromFile(imageFile))
              onSave(
                data.merge(
                  Immutable.fromJS({
                    edit,
                    src: imageUploadResponse.absoluteDownloadUrl,
                    width: imageUploadResponse.width,
                    height: imageUploadResponse.height,
                    refSrc: newSourceImageUploadResponse
                      ? newSourceImageUploadResponse.absoluteDownloadUrl
                      : data.get('refSrc'),
                  }),
                ),
              )
            }
          }}
        />
      )}
    </>
  )

  if (!editorView && isLinked) {
    const linkTarget = data.get('opentab') ? '_blank' : undefined
    const rel = linkTarget ? 'noopener noreferrer' : undefined

    return (
      <a
        href={data.get('link')}
        target={linkTarget}
        rel={rel}
        style={{ aspectRatio: data.getIn(['edit', 'aspectRatio', 'value']) || aspectRatio, ...wrapperStyle }}
        className={wrapperClassName}
      >
        {image}
      </a>
    )
  }

  return (
    <div
      style={{ aspectRatio: data.getIn(['edit', 'aspectRatio', 'value']) || aspectRatio, ...wrapperStyle }}
      className={wrapperClassName}
    >
      {image}
    </div>
  )
}

function getDataURLFromFile(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}
